import { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ExpanderOnChangeEvent } from 'src/components/sidebar';
import {
  collapseCard,
  expandCard,
  getIsCardCollapsed,
} from 'src/redux/pull-request/sidebar-card-collapsed-reducer';
import store from 'src/utils/store';

export type IsCardCollapsedProps = {
  isCardCollapsed: boolean;
  setIsCardCollapsed: (e: ExpanderOnChangeEvent) => void;
};
type IsCardCollapsedParams = {
  localStorageKey: string;
  defaultIsCardCollapsed?: boolean;
};
export const useIsCardCollapsed = ({
  localStorageKey,
  defaultIsCardCollapsed = false,
}: IsCardCollapsedParams) => {
  const localStorageState = store.get(localStorageKey, undefined);

  // get current collapse state
  const reduxCollapsedState = useSelector(state =>
    getIsCardCollapsed(state, localStorageKey)
  );
  // use default value if no current state
  let isCardCollapsed = reduxCollapsedState;
  if (isCardCollapsed === undefined) {
    isCardCollapsed =
      localStorageState === undefined
        ? defaultIsCardCollapsed
        : localStorageState;
  }
  const dispatch = useDispatch();

  // create callback that sets new collapse state in Redux
  const setIsCardCollapsed = useCallback(
    (e: ExpanderOnChangeEvent) =>
      e.isCollapsed
        ? dispatch(collapseCard(localStorageKey))
        : dispatch(expandCard(localStorageKey)),
    [dispatch, localStorageKey]
  );

  // updates local storage anytime state changes
  useEffect(() => {
    store.setStrictlyNecessaryItem(localStorageKey, isCardCollapsed);
  }, [localStorageKey, isCardCollapsed]);
  // update Redux with initial state (just the first time this renders)
  useEffect(() => {
    setIsCardCollapsed({ isCollapsed: isCardCollapsed });
    // eslint-disable-next-line
  }, []);

  return { isCardCollapsed, setIsCardCollapsed };
};
